import { z } from 'zod'
import { pageRequestSchema } from './page.contract'

/**
 * Wire shape for filterable/sortable list endpoints backing the web's SelectRecord tables
 * (packages/web/src/components/ui/SelectRecord). Maps 1:1 to the table's RecordSearchParams:
 * one optional filter per column (operator grammar: contains | eq | neq | gt | lt) plus a
 * single-column sort. Endpoints instantiate the schema with their filterable field names;
 * the server side translates via a column map (see record-query.ts). Named *.contract.ts
 * because oRPC contracts import it (web bundle guard) — keep it dependency-free beyond zod.
 */

export const RECORD_QUERY_OPERATOR_VALUES = ['contains', 'eq', 'neq', 'gt', 'lt'] as const
export type RecordQueryOperator = (typeof RECORD_QUERY_OPERATOR_VALUES)[number]

export const RECORD_QUERY_DIR_VALUES = ['asc', 'desc'] as const
export type RecordQueryDir = (typeof RECORD_QUERY_DIR_VALUES)[number]

export type RecordQueryFilter<F extends string = string> = {
  field: F
  operator: RecordQueryOperator
  operand: string
}

export type RecordQueryOrder<F extends string = string> = {
  field: F
  dir: RecordQueryDir
}

/** `{ page, filters, order }` input schema over the given filterable/sortable field names. */
export function recordQuerySchema<const F extends readonly [string, ...string[]]>(fields: F) {
  const field = z.enum(fields)
  return z.object({
    page: pageRequestSchema,
    filters: z
      .array(
        z.object({
          field,
          operator: z.enum(RECORD_QUERY_OPERATOR_VALUES),
          operand: z.string().min(1),
        }),
      )
      .default([]),
    order: z.object({ field, dir: z.enum(RECORD_QUERY_DIR_VALUES) }).optional(),
  })
}
